import React, { FC, useCallback, useEffect, useState } from 'react'
import { Dialog } from 'primereact/dialog'
import { useNavigate } from 'react-router-dom'
import { useToast } from '../../../common/toast/hooks/useToast'
import { Translation, TranslationFormProps } from './Translations.d'
import { Card } from 'primereact/card'
import useAxiosPrivate from '../../../../hooks/useAxiosPrivate'
import Common from '../../../common/Common'
import { Button, Alert, Row, Col, Form } from 'react-bootstrap'
import { useAppSelector } from '../../../../store/hooks'
import PageHeader from '../../../layout/page/PageHeader'
import { Formik, Form as FormikForm } from 'formik'
import * as Yup from 'yup'
import { Language } from '../../../../features/security/security.slice'
import { handleAxiosErrors } from '../../../common/forms/errors/Helper'
import RenderFieldsErrors from '../../../common/forms/errors/RenderFieldsErrors'
import RenderTranslatedFields from '../../../common/forms/fields/translated-fields/RenderTranslatedFields'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons'
import FormActions from '../../../common/forms/FormActions'
import RenderFormikErrors from '../../../common/forms/errors/RenderFormikErrors'
import { getGroupTranslation } from '../../../common/utility/helpers'

const TranslationForm: FC<TranslationFormProps> = ({
    id = null,
    inPopup = true,
    config = {},
}) => {
    const isViewOrEdit = id !== null
    const theme = useAppSelector((state) => state.theme.theme)
    const initData = useAppSelector((state: any) => state.security.initData)

    const navigate = useNavigate()
    const axiosPrivate = useAxiosPrivate()

    const emptyTranslation: Translation = {
        code: '',
        is_html: false,
    }

    const [translationDialog, setTranslationDialog] = useState(true)
    const [translation, setTranslation] =
        useState<Translation>(emptyTranslation)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isLoading, setIsLoading] = useState(true)
    const [errMsg, setErrMsg] = useState('')
    const [errorFields, setErrorFields] = useState<any>({})

    let schema = {
        code: Yup.string().trim().required('Required'),
    }

    let _initialValues = {
        ...translation,
    }

    Object.values(initData.languages.data).map((lang: Language) => {
        schema[`code_${lang.id}`] = Yup.string()
            .trim()
            .required(`The Translation (${lang.name}) field is required`)
        _initialValues[`code_${lang.id}`] = ''
        return true
    })

    const [initialValues, setInitialValues] = useState(_initialValues)

    const TranslationFormSchema = Yup.object().shape(schema)

    const { triggerToast } = useToast()

    const loadPage = useCallback(async () => {
        setErrMsg('')
        setErrorFields({})
        setIsLoading(true)

        const routePath = isViewOrEdit
            ? '/administration/languages/general-translations/' + id
            : '/administration/languages/general-translations/new'

        try {
            const response = await axiosPrivate.get(routePath).finally(() => {
                setIsLoading(false)
            })
            if (response.data?.success === true) {
                if (response.data.data.translation) {
                    setTranslation(response.data.data.translation)

                    setInitialValues((prevState) => {
                        return {
                            ...prevState,
                            ...response.data.data.translation,
                        }
                    })

                    Object.keys(initData.languages.data).map((locale) => {
                        setTranslation((prevState) => {
                            const lang: Language =
                                initData.languages.data[locale]

                            const groupTranslations = getGroupTranslation(
                                response.data.data.translations,
                                [
                                    {
                                        key: 'code',
                                        code: response.data.data.translation
                                            .code,
                                    },
                                ],
                                locale
                            )

                            setInitialValues((prevState) => {
                                return {
                                    ...prevState,
                                    [`code_${lang.id}`]:
                                        groupTranslations['code'],
                                }
                            })

                            return {
                                ...prevState,
                                [`code_${lang.id}`]: groupTranslations['code'],
                            }
                        })

                        return true
                    })
                }
            } else {
                setErrMsg(response.data?.errors.join('<br />'))
            }
        } catch (err) {
            // TODO: Sort out error handling!
            if (!err?.response) {
                setErrMsg('No Server Response')
            } else if (err.response?.status === 404) {
                setErrMsg('Record was not found')
            } else {
                setErrMsg('Failed to load translation')
            }
        }
    }, [axiosPrivate, id, initData.languages.data, isViewOrEdit])

    useEffect(() => {
        loadPage()
    }, [loadPage])

    const saveRecord = async (record) => {
        setErrMsg('')
        setIsSubmitting(true)

        try {
            const routePath = isViewOrEdit
                ? '/administration/languages/general-translations/' + id
                : '/administration/languages/general-translations/create'
            const response = await axiosPrivate
                .post(routePath, record, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                })
                .finally(() => {
                    setIsSubmitting(false)
                })

            if (response.data?.success === true) {
                triggerToast({
                    severity: 'success',
                    summary: config?.submitSuccessMsg?.header || 'Successful',
                    detail:
                        config?.submitSuccessMsg?.message ||
                        'Processed successfully',
                })
                hideDialog()
            } else {
                setErrMsg(response.data?.errors.join('<br />'))
            }
        } catch (err) {
            handleAxiosErrors(err, setErrMsg, setErrorFields, config)
        }
    }

    const hideDialog = () => {
        setTranslationDialog(false)
        navigate('/administration/translations')
    }

    const formFooter = (
        <React.Fragment>
            <Button
                variant={theme === 'dark' ? 'dark' : 'light'}
                onClick={hideDialog}
            >
                <span className="small">Cancel</span>
            </Button>
            <Button
                variant="success"
                disabled={isSubmitting || isLoading}
                // onClick={saveRecord}
                onClick={() => {
                    const formSubmitGtn = document.getElementById(
                        'translation-form-submit-btn'
                    )
                    formSubmitGtn.click()
                }}
            >
                <span className="small">
                    {isSubmitting ? (
                        <>
                            <FontAwesomeIcon icon={faCircleNotch} spin={true} />{' '}
                            {config?.saveButton?.savingLabel || 'Processing...'}
                        </>
                    ) : (
                        config?.saveButton?.label || 'Process'
                    )}
                </span>
            </Button>
        </React.Fragment>
    )

    const FormContent = (
        <>
            {isLoading && <Common.Loader text="Loading..." />}
            {errMsg && (
                <Alert show={true} variant="danger">
                    <Alert.Heading className="d-inline-block me-2">
                        Error!
                    </Alert.Heading>
                    <RenderFieldsErrors
                        errorFields={errorFields}
                        errMsg={errMsg}
                    />
                </Alert>
            )}
            <Formik
                initialValues={initialValues}
                validationSchema={TranslationFormSchema}
                onSubmit={(values) => {
                    saveRecord(values)
                }}
                enableReinitialize={true}
            >
                {(formikProps) => {
                    return (
                        <FormikForm id="translation-form">
                            <RenderFormikErrors formikProps={formikProps} />
                            <fieldset>
                                <legend>
                                    Translation{' '}
                                    <Form.Group
                                        className="ms-2 d-inline-block"
                                        controlId={'formTranslationIsHTMLFlag'}
                                    >
                                        <Form.Switch
                                            type="checkbox"
                                            label="HTML?"
                                            name="is_html"
                                            checked={formikProps.values.is_html}
                                            onChange={(e) =>
                                                formikProps.setFieldValue(
                                                    'is_html',
                                                    e.target.checked
                                                )
                                            }
                                        />
                                    </Form.Group>
                                </legend>
                                <Row>
                                    <Col xl={12}>
                                        <RenderTranslatedFields
                                            id="translationFormCode"
                                            formikProps={formikProps}
                                            fields={[
                                                {
                                                    id: 'formCode',
                                                    type: 'textarea',
                                                    name: 'code',
                                                    label: 'Translation',
                                                    showLabel: false,
                                                    placeholder:
                                                        'Enter button label',
                                                    enableEditor:
                                                        formikProps.values
                                                            .is_html,
                                                    required: true,
                                                },
                                            ]}
                                        />
                                    </Col>
                                </Row>
                            </fieldset>
                            <input
                                type="submit"
                                id="translation-form-submit-btn"
                                className="d-none"
                            />
                        </FormikForm>
                    )
                }}
            </Formik>
        </>
    )

    return (
        <>
            {inPopup ? (
                <Card className="flex justify-content-center">
                    <Dialog
                        visible={translationDialog}
                        breakpoints={{ '960px': '100vw', '640px': '100vw' }}
                        style={{ width: '60vw' }}
                        header={
                            isViewOrEdit
                                ? 'Edit translation'
                                : 'Create new translation'
                        }
                        modal
                        className="p-fluid"
                        footer={formFooter}
                        onHide={hideDialog}
                        maximizable
                        contentClassName={isLoading ? 'wrapped-content' : ''}
                        closeOnEscape={false}
                    >
                        {FormContent}
                    </Dialog>
                </Card>
            ) : (
                <>
                    <PageHeader
                        header={
                            isViewOrEdit
                                ? 'Edit translation'
                                : 'Create new translation'
                        }
                    />
                    {FormContent}
                    <FormActions formFooter={formFooter} />
                </>
            )}
        </>
    )
}

export default TranslationForm
